import {
  CreateReducer,
  CreateAction,
} from '../../store/utils/store'

import {
  getProjectHandlers,
} from '../../store/utils/api'

import {
  NopressState,
  ReducerHandler,
  ThunkHandler,
} from '../../types/store'

import {
  IRecord_Job,
} from '@kaidam/fc-shared/src/types/schema/job'

import {
  INopress_Template_Config,
  INopress_Installation_Config,
  IRecord_Nopress_Page_Core_Data,
  IRecord_Nopress_Pod_Core_Data,
  IRecord_Nopress_Publish,
  IQuery_Nopress_Website,
} from '@kaidam/fc-shared/src/types/schema/nopress'

import {
  IData_Nopress_List_Data,
  INopress_Sidebar_Item,
} from '@kaidam/fc-shared/src/types/nopress'

import {
  NetworkThunk,
} from '../utils/network'

import {
  ROLE_SCOPE_DESCENDENTS,
  CRM_EVENT_TYPE_NOPRESS_FORM,
} from '@kaidam/fc-shared/src/constants'

import nopressSelectors from '../selectors/nopress'
import projectSelectors from '../selectors/project'
import routerSelectors from '../selectors/router'
import routerActions from './router'
import snackbarActions from './snackbar'

const prefix = 'nopress'

/*

  initial state

*/
const initialState: NopressState = {
  config: {
    websites: [],
    templates: {},
    rootProvider: '',
  },
  data: {
    pages: [],
    pods: [],
  },
  templateWithData: null,
  dataLoaded: false,
  expandedFolders: {},
  publishJobs: [],
}

/*

  reducers

*/
const setConfig: ReducerHandler<NopressState, INopress_Installation_Config> = (state, action) => {
  state.config = action.payload
}

const setTemplateWithData: ReducerHandler<NopressState, INopress_Template_Config> = (state, action) => {
  state.templateWithData = action.payload
}

const setData: ReducerHandler<NopressState, IData_Nopress_List_Data> = (state, action) => {
  state.data = action.payload
}

const setDataLoaded: ReducerHandler<NopressState, boolean> = (state, action) => {
  state.dataLoaded = action.payload
}

const setExpandedFolders: ReducerHandler<NopressState, Record<string, boolean>> = (state, action) => {
  state.expandedFolders = action.payload
}

const setExpandedFolder: ReducerHandler<NopressState, {
  id: string,
  value: boolean,
}> = (state, action) => {
  const {
    id,
    value,
  } = action.payload
  state.expandedFolders[id] = value
}

const setPublishJobs: ReducerHandler<NopressState, IRecord_Nopress_Publish[]> = (state, action) => {
  state.publishJobs = action.payload
}

/*

  thunks

*/
const loadConfig: ThunkHandler<void, void> = () => async (dispatch, getState) => {
  const orgId = projectSelectors.currentOrgId(getState())
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<INopress_Installation_Config>(`/nopress/${orgId}/installation`)
  dispatch(actions.setConfig(data))
}

const loadPublishJobs: ThunkHandler<{
  orgid: string,
  website: string,
}, void> = ({
  orgid,
  website,
}) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<IRecord_Nopress_Publish[]>(`/nopress/${orgid}/${website}/publishJobs`)
  dispatch(actions.setPublishJobs(data))
}

const list: ThunkHandler<{
  orgid: string,
  website: string,
}, void> = ({orgid, website}) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<IData_Nopress_List_Data>(`/nopress/${orgid}/${website}/list`)
  dispatch(actions.setData(data))
}

const loadTemplateWithData: ThunkHandler<{
  orgid: string,
  website: string,
}, void> = ({orgid, website}) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<INopress_Template_Config>(`/nopress/${orgid}/${website}/templateWithData`)
  dispatch(actions.setTemplateWithData(data))
}

const openId: ThunkHandler<{
  id: string,
  type: string
}, void> = ({
  id,
  type,
}) => async (dispatch, getState) => {
  const params = routerSelectors.params(getState())
  if(params.id == id) return
  if(type == 'form') {
    const formid = id.replace(/^widgets_forms_/, '')
    dispatch(routerActions.navigateTo('crm.events', {
      orgid: params.orgid,
      scope: ROLE_SCOPE_DESCENDENTS,
      type: CRM_EVENT_TYPE_NOPRESS_FORM,
      group: formid,
    }))
  }
  else {
    const expandedFolders = nopressSelectors.expandedFolders(getState())
    let newExpanded = true
    if(params.id == id) {
      newExpanded = expandedFolders[id] ? false : true
    }
    dispatch(actions.setExpandedFolder({
      id,
      value: newExpanded
    }))
    dispatch(routerActions.mergeQueryParams({
      type,
      id,
    }))
  }
}

const openItem: ThunkHandler<INopress_Sidebar_Item, void> = (item) => async (dispatch, getState) => {
  dispatch(actions.openId({
    id: item.id,
    type: item.type,
  }))
}

const publish: ThunkHandler<{
  orgid: string,
  website: string,
}, IRecord_Job<IQuery_Nopress_Website>> = ({
  orgid,
  website,
}) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const result = await handlers.post<void, IRecord_Job<IQuery_Nopress_Website>>(`/nopress/${orgid}/${website}/publish`)
  return result
}

const savePage: ThunkHandler<{
  orgid: string,
  website: string,
  pageid: string,
  id: string,
  data: IRecord_Nopress_Page_Core_Data,
}, boolean> = ({
  orgid,
  website,
  pageid,
  id,
  data,
}) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  let snackbarMessage = id == 'new' ? 'Page Created' : 'Page Updated'
  if(id == 'new') {
    await handlers.post(`/nopress/${orgid}/${website}/page/${pageid}`, data)
  }
  else {
    await handlers.put(`/nopress/${orgid}/${website}/page/${pageid}/${id}`, data)
  }
  await dispatch(actions.loadTemplateWithData({
    orgid,
    website,
  }))
  dispatch(snackbarActions.setSuccess(snackbarMessage))
  return true
}

const savePod: ThunkHandler<{
  orgid: string,
  website: string,
  pageid: string,
  podid: string,
  id: string,
  data: IRecord_Nopress_Pod_Core_Data,
}, boolean> = ({
  orgid,
  website,
  pageid,
  podid,
  id,
  data,
}) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  let snackbarMessage = id == 'new' ? 'Pod Created' : 'Pod Updated'
  if(id == 'new') {
    await handlers.post(`/nopress/${orgid}/${website}/pod/${pageid}/${podid}`, data)
  }
  else {
    await handlers.put(`/nopress/${orgid}/${website}/pod/${pageid}/${podid}/${id}`, data)
  }
  await dispatch(actions.loadTemplateWithData({
    orgid,
    website,
  }))
  dispatch(snackbarActions.setSuccess(snackbarMessage))
  return true
}

const deletePage: ThunkHandler<{
  orgid: string,
  website: string,
  pageid: string,
  id: string,
}, boolean> = ({
  orgid,
  website,
  pageid,
  id,
}) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  await handlers.delete(`/nopress/${orgid}/${website}/page/${pageid}/${id}`)
  await dispatch(actions.loadTemplateWithData({
    orgid,
    website,
  }))
  dispatch(snackbarActions.setSuccess(`page deleted`))
  return true
}

const deletePod: ThunkHandler<{
  orgid: string,
  website: string,
  pageid: string,
  podid: string,
  id: string,
}, boolean> = ({
  orgid,
  website,
  pageid,
  podid,
  id,
}) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  await handlers.delete(`/nopress/${orgid}/${website}/pod/${pageid}/${podid}/${id}`)
  await dispatch(actions.loadTemplateWithData({
    orgid,
    website,
  }))
  dispatch(snackbarActions.setSuccess(`pod deleted`))
  return true
}

/*

  collections

*/
const reducers = {
  setConfig,
  setTemplateWithData,
  setData,
  setDataLoaded,
  setExpandedFolders,
  setExpandedFolder,
  setPublishJobs,
}

export const actions = {
  setConfig: CreateAction(prefix, `setConfig`, setConfig),
  setTemplateWithData: CreateAction(prefix, `setTemplateWithData`, setTemplateWithData),
  setData: CreateAction(prefix, `setData`, setData),
  setDataLoaded: CreateAction(prefix, `setDataLoaded`, setDataLoaded),
  setExpandedFolders: CreateAction(prefix, `setExpandedFolders`, setExpandedFolders),
  setExpandedFolder: CreateAction(prefix, `setExpandedFolder`, setExpandedFolder),
  setPublishJobs: CreateAction(prefix, `setPublishJobs`, setPublishJobs),
  publish: NetworkThunk(prefix, 'publish', publish, {
    snackbar: true,
  }),
  loadConfig: NetworkThunk(prefix, 'loadConfig', loadConfig, {
    snackbar: true,
  }),
  loadPublishJobs: NetworkThunk(prefix, 'loadPublishJobs', loadPublishJobs, {
    snackbar: true,
  }),
  loadTemplateWithData: NetworkThunk(prefix, 'loadTemplateWithData', loadTemplateWithData, {
    snackbar: true,
  }),
  list: NetworkThunk(prefix, 'list', list, {
    snackbar: true,
  }),
  openId,
  openItem,
  savePage: NetworkThunk(prefix, 'savePage', savePage, {
    snackbar: true,
  }),
  savePod: NetworkThunk(prefix, 'savePod', savePod, {
    snackbar: true,
  }),
  deletePage: NetworkThunk(prefix, 'deletePage', deletePage, {
    snackbar: true,
  }),
  deletePod: NetworkThunk(prefix, 'deletePod', deletePod, {
    snackbar: true,
  }),
}

export const reducer = CreateReducer<NopressState>({
  prefix,
  initialState,
  reducers,
})

export default actions